import { StatusPill } from "@solomon-apps/shared/src/components/Metrics/StatusPill";
import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { CheckIcon } from "@solomon-labs/design/components/icons";
import type { ReactElement } from "react";

import {
    type ApplicationPhase,
    type ApplicationState,
    type NextStepTarget,
    type OnboardingNode,
    type OnboardingSubsection,
    orderByCategory,
    type PreconditionRef,
    type SectionStatus,
    type Slot,
    type SlotReviewState,
    type SlotStatus,
    type SubsectionId
} from "./types";

/**
 * Shared primitives for the onboarding hub.
 *
 * Status colors, inline estimate logic, locked-copy helpers, the
 * StatusBadge, the EstimateLine, the PreconditionsLine, and the
 * slot-numbering / visibility helpers all live here.
 */

// ─── Status tone + label ─────────────────────────────────────────────────────

/** Badge text tone — matches the IndicationUnit dot color emitted by
 *  `statusToIndicationState` so the text and the dot read as a single
 *  unit. The mapping is one-for-one with that helper:
 *
 *    locked / NotStarted        → disabled (muted)
 *    InProgress / WaitingOnSolomon → pending (warning)
 *    WaitingOnYou / NeedsAttention → error
 *    Complete                   → success
 */
export const BADGE_TONE: Record<string, { text: string; dot: string }> = {
    locked: { text: "text-disabled-base", dot: "bg-disabled-base" },
    NotStarted: { text: "text-disabled-base", dot: "bg-disabled-base" },
    InProgress: { text: "text-warning-base", dot: "bg-warning-base" },
    WaitingOnYou: { text: "text-error-base", dot: "bg-error-base" },
    WaitingOnSolomon: { text: "text-warning-base", dot: "bg-warning-base" },
    NeedsAttention: { text: "text-error-base", dot: "bg-error-base" },
    Complete: { text: "text-success-base", dot: "bg-success-base" }
};

/**
 * Map onboarding section statuses + lock state to the IndicationUnit
 * `state` enum (`disabled` / `success` / `pending` / `error`). Mapping:
 *
 *   Complete                                  → success
 *   WaitingOnYou / NeedsAttention             → error   (ember tone)
 *   InProgress / WaitingOnSolomon             → pending (warning tone)
 *   NotStarted / locked                       → disabled
 *
 * Mirrors the source IndicationUnit's status palette so the dot color
 * stays in sync with the StatusBadge text tone above.
 */
export type IndicationState = "disabled" | "success" | "pending" | "error";
export function statusToIndicationState(
    status: SectionStatus,
    isLocked: boolean
): IndicationState {
    if (isLocked) return "disabled";
    if (status === "Complete") return "success";
    if (status === "WaitingOnYou" || status === "NeedsAttention")
        return "error";
    if (status === "InProgress" || status === "WaitingOnSolomon")
        return "pending";
    return "disabled";
}

/**
 * Phase-scoped label vocabulary for section status badges. Each
 * (phase, status) pair has exactly one label so the UI never overloads
 * a single word across phases — e.g. `Complete` reads "Completed" while
 * the applicant is drafting and "Approved" once the reviewer has
 * signed off. Both phases keep the same indicator markers
 * (success/pending/error/disabled — see BADGE_TONE + statusToIndicationState).
 *
 *   submission — drafting vocab, no error state (the form refuses
 *     errored inputs)
 *   review     — reviewer-driven vocab; covers terminal states too
 *     (Approved / Declined still read in this vocab)
 *
 * Statuses that "shouldn't appear" in a given phase are mapped to a
 * sensible fallback so an unexpected scenario doesn't crash; they're
 * never the authoritative path.
 */
export const BADGE_LABEL: Record<
    ApplicationPhase,
    Record<SectionStatus, string>
> = {
    submission: {
        NotStarted: "Not Started",
        InProgress: "In Progress",
        WaitingOnYou: "In Progress",
        WaitingOnSolomon: "In Progress",
        NeedsAttention: "In Progress",
        Complete: "Completed"
    },
    review: {
        NotStarted: "Under Review",
        InProgress: "Under Review",
        WaitingOnYou: "Action Required",
        WaitingOnSolomon: "Under Review",
        NeedsAttention: "Action Required",
        Complete: "Approved"
    }
};

/**
 * Compute the section's status from its slots' review states.
 *
 * Pre-submission (`appState` is `NotStarted` / `InProgress`) the
 * scenario-driven `subsection.status` stays authoritative — sections
 * track their pre-submission progress through the existing six
 * statuses.
 *
 * Post-submission, `subsection.status` becomes a *roll-up* of the
 * slots' `reviewState` values:
 *   - All slots `Approved` (and no unfulfilled `issuerRequested`
 *     slots) → `Complete`
 *   - Any slot `Rejected` OR any unfulfilled `issuerRequested` slot
 *     → `NeedsAttention`
 *   - Otherwise (any mix of `PendingReview` with `Approved`) →
 *     `WaitingOnSolomon`
 *
 * Sections with no slots (item-list subsections like Wallets / UBOs
 * before any are added, or single-action subsections like the MSA)
 * fall through to the scenario-driven `subsection.status`.
 */
export function computeSectionStatusFromSlots(
    subsection: OnboardingSubsection,
    appState: ApplicationState
): SectionStatus {
    if (
        appState === "NotStarted" ||
        appState === "InProgress" ||
        !subsection.isApplicable ||
        subsection.slots.length === 0
    ) {
        return subsection.status;
    }
    const states = subsection.slots
        .map((s) => s.reviewState)
        .filter((s): s is SlotReviewState => s !== undefined);
    if (states.length === 0) return subsection.status;

    const hasUnfulfilledAdhoc = subsection.slots.some(
        (s) => s.issuerRequested !== undefined && s.reviewState !== "Approved"
    );
    const hasRejected = states.some((s) => s === "Rejected");
    if (hasRejected || hasUnfulfilledAdhoc) return "NeedsAttention";

    const allApproved = states.every((s) => s === "Approved");
    if (allApproved) return "Complete";

    return "WaitingOnSolomon";
}

export function statusBadgeLabel(
    subsection: OnboardingSubsection,
    phase: ApplicationPhase
): string {
    // Locked sections fall through to their underlying status label
    // (typically "Not Started") — the disabled indicator color is the
    // signal that the section is gated, the label doesn't need to
    // re-state it. Keeps locked + freshly-not-started sections reading
    // identically.
    if (subsection.treatment === "deferred") return "Available later";
    if (subsection.treatment === "solomon-run") {
        if (subsection.status === "Complete") {
            return phase === "review" ? "Approved" : "Completed";
        }
        if (
            subsection.status === "InProgress" ||
            subsection.status === "WaitingOnSolomon"
        ) {
            return phase === "review" ? "Under Review" : "In Progress";
        }
        return "Runs at submission";
    }
    return BADGE_LABEL[phase][subsection.status];
}

// ─── Lock detection ──────────────────────────────────────────────────────────

export interface LockComputed {
    hasPreconditions: boolean;
    allPreconditionsMet: boolean;
    isLocked: boolean;
    isDeferred: boolean;
    isSolomonRun: boolean;
    interactive: boolean;
}

export function computeLockState(
    node: OnboardingNode,
    applicationFrozen: boolean
): LockComputed {
    const subsection = node.subsection;
    const hasPreconditions =
        !!node.preconditions && node.preconditions.length > 0;
    const allPreconditionsMet =
        hasPreconditions &&
        node.preconditions!.every((p) => p.status === "Complete");
    const preconditionsLocked =
        hasPreconditions &&
        subsection.status === "NotStarted" &&
        !allPreconditionsMet;
    const isLocked = !!subsection.prerequisiteLabel || preconditionsLocked;
    const isDeferred = subsection.treatment === "deferred";
    const isSolomonRun = subsection.treatment === "solomon-run";
    const interactive =
        !isLocked &&
        !isDeferred &&
        !isSolomonRun &&
        !(applicationFrozen && subsection.status === "WaitingOnSolomon");
    return {
        hasPreconditions,
        allPreconditionsMet,
        isLocked,
        isDeferred,
        isSolomonRun,
        interactive
    };
}

export function joinTitlesWithAnd(titles: string[]): string {
    if (titles.length === 0) return "";
    if (titles.length === 1) return titles[0];
    if (titles.length === 2) return `${titles[0]} and ${titles[1]}`;
    return `${titles.slice(0, -1).join(", ")}, and ${titles[titles.length - 1]}`;
}

export function stripSectionNumber(label: string): string {
    return label.replace(/^§?[A-Z]?\.?\d+(?:\.\d+)?\s+/i, "").trim() || label;
}

export function lockedGuidanceTitle(
    subsection: OnboardingSubsection,
    node: OnboardingNode
): string | undefined {
    if (subsection.prerequisiteLabel) {
        return stripSectionNumber(subsection.prerequisiteLabel);
    }
    if (node.preconditions && node.preconditions.length > 0) {
        const incomplete = node.preconditions.filter(
            (p) => p.status !== "Complete"
        );
        if (incomplete.length > 0) {
            return joinTitlesWithAnd(incomplete.map((p) => p.title));
        }
    }
    return undefined;
}

/** Roll up legal-entity + tax_identification into one applicant-facing step. */
export function rollupCompanySubsectionStatus(
    legal: SectionStatus | undefined,
    tax: SectionStatus | undefined
): SectionStatus {
    const statuses = [legal, tax].filter(
        (s): s is SectionStatus => s !== undefined
    );
    if (statuses.length === 0) return "NotStarted";
    const rank: Record<SectionStatus, number> = {
        NeedsAttention: 5,
        WaitingOnYou: 4,
        InProgress: 3,
        WaitingOnSolomon: 2,
        NotStarted: 1,
        Complete: 0
    };
    return statuses.reduce((worst, s) => (rank[s] > rank[worst] ? s : worst));
}

export function showInlineEstimate(
    subsection: OnboardingSubsection,
    isLocked: boolean
): boolean {
    if (isLocked) return false;
    if (subsection.treatment === "deferred") return false;
    if (subsection.treatment === "solomon-run") return false;
    if (subsection.status === "Complete") return false;
    if (subsection.status === "WaitingOnSolomon") return false;
    return !!subsection.estimateLabel;
}

// Whose-turn pointer — the subsection that should auto-focus / auto-expand.
export function findFocusTarget(
    nodes: OnboardingNode[]
): SubsectionId | undefined {
    const priorityStatuses: SectionStatus[] = [
        "NeedsAttention",
        "WaitingOnYou",
        "InProgress"
    ];
    for (const status of priorityStatuses) {
        for (const n of nodes) {
            if (n.subsection.status === status) return n.subsection.id;
            for (const c of n.children) {
                if (c.status === status) return c.id;
            }
        }
    }
    return nodes[0]?.subsection.id;
}

/**
 * The next thing left to do after the row the applicant is on, walking the
 * rail top-to-bottom in render order — section rows and their sub-rows
 * alike. Drives the "Continue to ..." CTA at the bottom of every Save form.
 *
 * Sub-rows are part of the walk because they are part of the work: a
 * section stays incomplete until each of its required slots is filled, and
 * a walk over section rows alone would step straight past an unfilled
 * upload into the next section (the applicant then has no path back to it
 * short of noticing it on the rail).
 *
 * Skipped as destinations: slots with nothing to derive a state from
 * (item-list instances), `Optional` slots, and the slot rows of a
 * subsection that auto-collapses into its single slot — clicking that
 * subsection's row already routes there.
 */
export function findNextIncompleteTarget(
    orderedNodes: OnboardingNode[],
    focus: { id: SubsectionId; slotIdx?: number },
    triggers: Record<string, boolean> | undefined,
    collapsedSlotIdxByParent: Map<SubsectionId, number>
): NextStepTarget | null {
    interface Row {
        target: NextStepTarget;
        incomplete: boolean;
    }
    const rows: Row[] = [];

    const pushSubsection = (subsection: OnboardingSubsection): void => {
        rows.push({
            target: { id: subsection.id, title: subsection.title },
            incomplete: subsection.status !== "Complete"
        });
        if (subsection.slotsAsItems) return;
        if (collapsedSlotIdxByParent.has(subsection.id)) return;
        subsection.slots.forEach((slot, slotIdx) => {
            if (!isSlotVisible(slot, triggers)) return;
            rows.push({
                target: { id: subsection.id, title: slot.title, slotIdx },
                incomplete: slot.status === "NotStarted"
            });
        });
    };

    for (const node of orderedNodes) {
        pushSubsection(node.subsection);
        for (const child of node.children) pushSubsection(child);
    }

    // A subsection that auto-collapses into its single slot has no slot
    // row on the rail — focus on that slot is focus on the section row.
    const collapsedIdx = collapsedSlotIdxByParent.get(focus.id);
    const from =
        collapsedIdx !== undefined && collapsedIdx === focus.slotIdx
            ? { id: focus.id }
            : focus;

    const currentIdx = rows.findIndex(
        ({ target }) => target.id === from.id && target.slotIdx === from.slotIdx
    );
    if (currentIdx === -1) return null;
    return (
        rows.slice(currentIdx + 1).find((row) => row.incomplete)?.target ?? null
    );
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

/**
 * Onboarding-surface wrapper around `<StatusPill>`. Handles the phase-
 * aware label vocabulary (`statusBadgeLabel`: submission vocab on the
 * client side, review vocab on the issuer side), the per-state text
 * tone (BADGE_TONE), and the optional hover treatment used by clickable
 * list rows.
 *
 * Composes `<StatusPill textVariant="label">` — the dot pair is sized
 * to `text-xs leading-4` typography per IndicationUnit's PAIR_DEFAULTS
 * `label` entry.
 */
export function StatusBadge({
    subsection,
    isLocked,
    phase,
    hoverable = false
}: {
    subsection: OnboardingSubsection;
    isLocked: boolean;
    /** Drives the label vocabulary — "Completed" vs "Approved", "In Progress"
     *  vs "Under Review", etc. The marker tone (success/pending/error/
     *  disabled) is unchanged across phases. */
    phase: ApplicationPhase;
    /** When true, the badge label flips to ember on the parent's group hover.
     *  Used by the list rows where the whole row tracks hover together. */
    hoverable?: boolean;
}): ReactElement {
    const tone = BADGE_TONE[isLocked ? "locked" : subsection.status];
    const label = statusBadgeLabel(subsection, phase);
    const indicationState = statusToIndicationState(
        subsection.status,
        isLocked
    );
    return (
        <StatusPill
            indicatorState={indicationState}
            textVariant="label"
            textClassName={`leading-none ${tone.text} transition-colors ${
                hoverable ? "group-hover:text-accent-base" : ""
            }`}
        >
            {label}
        </StatusPill>
    );
}

/** Submission-phase vocabulary for a slot's own badge. Deliberately
 *  narrower than `BADGE_LABEL`: a sub-row is a single deliverable, so it
 *  is either on file, not on file, or not required. */
const SLOT_BADGE_LABEL: Record<SlotStatus, string> = {
    NotStarted: "Not Started",
    Complete: "Completed",
    Optional: "Optional"
};

/**
 * Per-slot badge for the left rail. Renders only while the applicant is
 * drafting — once the application is submitted the reviewer's verdict
 * (`Slot.reviewState`) is the state that matters, and the section badge
 * carries the review vocabulary.
 *
 * Without this, a section could read "In Progress" with every one of its
 * sub-rows looking identical, which is exactly how a missing upload goes
 * unnoticed.
 */
export function SlotStatusBadge({
    status,
    hoverable = false
}: {
    status: SlotStatus;
    hoverable?: boolean;
}): ReactElement {
    const tone = BADGE_TONE[status === "Optional" ? "NotStarted" : status];
    return (
        <StatusPill
            indicatorState={status === "Complete" ? "success" : "disabled"}
            textVariant="label"
            textClassName={`leading-none ${tone.text} transition-colors ${
                hoverable ? "group-hover:text-accent-base" : ""
            }`}
        >
            {SLOT_BADGE_LABEL[status]}
        </StatusPill>
    );
}

// ─── Slot numbering + visibility ─────────────────────────────────────────────

/** Stable key for a slot row — `${subsectionId}::${slotIdx}`. */
export function slotKey(subsectionId: SubsectionId, slotIdx: number): string {
    return `${subsectionId}::${slotIdx}`;
}

/**
 * Whether a slot is visible under the active scenario triggers.
 *
 * Conditional slots are hidden by default (empty triggers map). When the
 * scenario flips a trigger on (e.g. `triggers["non-Hague-Convention
 * jurisdiction"] = true`), every slot whose `conditional.trigger` matches
 * becomes visible. Non-conditional slots are always visible.
 */
export function isSlotVisible(
    slot: Slot,
    triggers: Record<string, boolean> | undefined
): boolean {
    if (!slot.conditional) return true;
    if (!triggers) return false;
    return !!triggers[slot.conditional.trigger];
}

/**
 * Build the slot-numbering map for an entire onboarding application.
 *
 * Walking algorithm:
 *   For each top-level subsection at index i:
 *     topLevelNumber = i + 1   // 1, 2, 3, …
 *     slotCounter = 1
 *     for each visible slot in subsection.slots:
 *       map[`${id}::${slotIdx}`] = `${topLevelNumber}.${slotCounter++}`
 *     for each child of subsection:
 *       for each visible slot in child.slots:
 *         map[`${child.id}::${slotIdx}`] = `${topLevelNumber}.${slotCounter++}`
 *
 * Children continue the parent's namespace so Beneficial Ownership's slots
 * pick up where Entity Standing's slots left off (2.5, 2.6, …).
 *
 * Hidden conditional slots are skipped — they don't get a number and don't
 * advance the counter. The keys are `${subsectionId}::${slotIdx}` from the
 * raw `slots` array index so the renderer can look up by the same key it
 * iterates with.
 */
export function buildSlotNumberMap(
    nodes: OnboardingNode[],
    triggers: Record<string, boolean> | undefined
): Map<string, string> {
    const orderedTopLevel = orderByCategory(
        nodes,
        (node) => node.subsection.category
    );
    const map = new Map<string, string>();
    orderedTopLevel.forEach((node, i) => {
        const topLevelNumber = i + 1;
        let counter = 1;
        node.subsection.slots.forEach((slot, slotIdx) => {
            if (!isSlotVisible(slot, triggers)) return;
            map.set(
                slotKey(node.subsection.id, slotIdx),
                `${topLevelNumber}.${counter++}`
            );
        });
        for (const child of node.children) {
            // The child subsection header itself takes the next position
            // in the parent's slot sequence — so a child like "Ultimate
            // Beneficiaries" nested under legal-entity renders as 2.4
            // when the parent already has 2.1 / 2.2 / 2.3.
            map.set(child.id, `${topLevelNumber}.${counter++}`);
            // Child's own slots continue the counter unless the child is
            // in `slotsAsItems` mode — those slots render with status dots
            // instead of numbers and don't consume positions.
            if (child.slotsAsItems) continue;
            child.slots.forEach((slot, slotIdx) => {
                if (!isSlotVisible(slot, triggers)) return;
                map.set(
                    slotKey(child.id, slotIdx),
                    `${topLevelNumber}.${counter++}`
                );
            });
        }
    });
    return map;
}

// ─── PreconditionsLine ───────────────────────────────────────────────────────

export function PreconditionsLine({
    preconditions,
    allMet,
    onJump,
    tone
}: {
    preconditions: PreconditionRef[];
    allMet: boolean;
    onJump?: (id: SubsectionId) => void;
    tone: "locked" | "ready";
}): ReactElement {
    if (allMet) {
        return (
            <Text
                variant="label"
                className="leading-snug text-foreground-muted pt-2 inline-flex items-center gap-1.5"
            >
                <CheckIcon className="text-success-base size-3" />
                Ready to begin
            </Text>
        );
    }
    const incomplete = preconditions.filter((p) => p.status !== "Complete");
    const targets = incomplete.length > 0 ? incomplete : preconditions;
    return (
        <Text
            variant="label"
            className={`leading-snug pt-2 ${
                tone === "locked"
                    ? "text-foreground-neutral"
                    : "text-foreground-muted"
            }`}
        >
            Continue with{" "}
            {targets.map((p, i) => {
                const isLast = i === targets.length - 1;
                const isSecondLast = i === targets.length - 2;
                let connector = "";
                if (targets.length === 2 && i === 0) connector = " and ";
                else if (targets.length > 2 && isSecondLast)
                    connector = ", and ";
                else if (!isLast) connector = ", ";
                return (
                    <span key={p.refId}>
                        <Button
                            type="button"
                            variant="transparent"
                            interaction="underline"
                            size="fit"
                            onClick={(e) => {
                                e.stopPropagation();
                                onJump?.(p.refId);
                            }}
                            className="text-foreground-muted hover:text-accent-base"
                        >
                            {p.title}
                        </Button>
                        {connector}
                    </span>
                );
            })}{" "}
            first
        </Text>
    );
}

// ─── EstimateLine ────────────────────────────────────────────────────────────

export function EstimateLine({
    subsection,
    locked,
    compact = false,
    parentTitle
}: {
    subsection: OnboardingSubsection;
    locked: boolean;
    compact?: boolean;
    parentTitle?: string;
}): ReactElement | null {
    if (subsection.status === "Complete") return null;
    const padding = compact ? "pt-2" : "pt-2.5";
    if (locked) {
        if (!parentTitle) return null;
        return (
            <Text
                variant="label"
                className={`leading-snug ${padding} text-foreground-neutral`}
            >
                Continue with {parentTitle} first
            </Text>
        );
    }
    if (subsection.treatment === "deferred") {
        return (
            <Text
                variant="label"
                className={`leading-snug ${padding} text-foreground-imprint`}
            >
                Available later — Solomon is still preparing this path
            </Text>
        );
    }
    if (subsection.treatment === "solomon-run") {
        if (subsection.status === "InProgress" && subsection.slaLabel) {
            return (
                <Text
                    variant="label"
                    className={`leading-none ${padding} text-foreground-neutral`}
                >
                    {subsection.slaLabel}
                </Text>
            );
        }
        return null;
    }
    // WaitingOnSolomon intentionally returns nothing — the StatusBadge
    // already says "Under Review" / "Action Required"; a second copy
    // here under the description is redundant noise.
    if (subsection.status === "NeedsAttention" && subsection.estimateLabel) {
        return (
            <Text
                variant="label"
                className={`leading-none ${padding} text-error-base`}
            >
                {subsection.estimateLabel}
            </Text>
        );
    }
    return null;
}
